import { Item, ItemGroupPreview, ItemsResponseDTO, SubsystemsResponseDTO } from '@dto/architecture.dto';
import { Modal, Spin } from 'antd';
import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { XmTableScope } from "src/component/table/XmTable";
import { ColProps, XmTableEntry, XmTableProps } from "src/component/table/tableTypes";
import { getLabel } from 'src/content/search';
import { getAllSubsystems } from 'src/content/tools/alerting/alertingService';
import { API_URL } from 'src/data/Api';
import { Log } from 'src/service/log.service';
import { GLOB } from "src/util/Glob";
import { postApi } from 'src/util/apiCalls';
import { ApiPrams } from '../../XmRule';
import './Preview.less';

export function showPreviewModal(content: ReactNode) {
  GLOB.app.modal.info({
    className: 'xm-preview',
    icon: null,
    okButtonProps: { hidden: true },
    okText: 'Close',
    mask: { closable: true },
    width: 'auto',
    centered: true,
    closable: true,
    content
  });
}

export type UsePreviewModal = () => [(previewProps: Omit<Props, 'linuxExclude'>) => void, React.ReactElement];
export const usePreviewModal: UsePreviewModal = () => {
  const [modal, contextHolder] = Modal.useModal();

  function showModal(previewProps: Omit<Props, 'linuxExclude'>) {
    showPreviewModal(<Preview {...previewProps} />);
  }

  return [showModal, contextHolder];
};

export class ItemParentLabel extends Item {
  parentLabel: string;
}

export function makeItemLabel(item: Item): ItemParentLabel {
  return {
    ...item,
    parentLabel: item.root_parent?.label,
  };
}

type Dto = ItemGroupPreview | ItemGroupPreview[];

// set hw_type & subsystem if items will be of same type
interface Props {
  data: Dto;
  parent?: boolean;
  hw_type?: string;
  subsystem?: string;
  strictAcl?: boolean;
  additionalApiParams?: ApiPrams;
}

function Preview({ data, parent, strictAcl, additionalApiParams = {}, subsystem, hw_type }: Props) {
  const [items, setItems] = useState<ItemParentLabel[]>([]);
  const [loading, setLoading] = useState(true);
  const subsystemLabel = useRef(subsystem || 'Subsystem');
  const showParentColumn = useRef(true);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    function makeItems([items, subsLabels]: [AxiosResponse<ItemsResponseDTO, Dto>, AxiosResponse<SubsystemsResponseDTO>]) {
      const newItems: ItemParentLabel[] = [];
      if (subsystem && hw_type)
        subsystemLabel.current = getLabel('subsystem', subsLabels.data.data)(subsystem, ({ hw_type: hwType }) => (hw_type === hwType));
      else if (subsystem && items.data.data[0]?.hw_type)
        subsystemLabel.current = getLabel('subsystem', subsLabels.data.data)(subsystem, ({ hw_type: hwType }) => (items.data.data[0].hw_type === hwType));
      else
        subsystemLabel.current = subsLabels.data.data.find(({ subsystem: sub, class: cls }) => !Array.isArray(data) && data.class === cls && subsystem === sub).label;

      showParentColumn.current = items.data.data.some(item => item.root_parent);
      newItems.push(...items.data.data.map(makeItemLabel));
      setItems(newItems.sort((a, b) => (a.parentLabel + a.label).localeCompare(b.parentLabel + b.label)));
    }

    function getRegex(call: ApiCall<Dto>) {
      Promise.all([call(data, { signal: controller.signal, params: additionalApiParams }, strictAcl), getAllSubsystems()]).then(
        makeItems,
        (reason: AxiosError) => Log.error('Failed to get preview!', reason)
      ).finally(() => setLoading(false));
    }

    if (isNotMultiRegex(data))
      getRegex(regexApi);
    else
      getRegex(multiRegexApi);

    return () => controller.abort();
  }, []);

  if (loading)
    return <div className="xm-preview-loading"><Spin /></div>;

  return (
    <PreviewTable
      subsystemLabel={subsystemLabel.current}
      showParentColumn={showParentColumn.current}
      parent={parent}
      tableProps={{ dataSource: items }}
    />
  );
}

interface PreviewTableProps {
  tableProps: XmTableProps<ItemParentLabel & XmTableEntry>;
  subsystemLabel?: string;
  parent?: boolean;
  showParentColumn?: boolean;
}

export function PreviewTable({ subsystemLabel, parent, tableProps, showParentColumn = true }: PreviewTableProps) {
  const [tscope] = useState(() => new XmTableScope<ItemParentLabel>());

  const baseColumn = (dataIndex: string, title: string): ColProps<ItemParentLabel & XmTableEntry> => ({
    className: 'xm-col',
    title,
    dataIndex,
    minWidth: '8em',
    sorter: (l, r) => l.label.localeCompare(r.label),
    filter: { field: [dataIndex] },
    onCell: ({ deleted }: ItemParentLabel) => deleted ? { className: 'xm-item-deleted' } : {},
  });

  const columns: ColProps<ItemParentLabel & XmTableEntry>[] = parent ? [
    baseColumn('label', 'Parent')
  ] : showParentColumn ? [
    {
      className: 'xm-col',
      title: 'Parent',
      dataIndex: 'parentLabel',
      minWidth: '8em',
      sorter: (l, r) => l.parentLabel.localeCompare(r.parentLabel),
      filter: { field: ['parentLabel'] },
    },
    baseColumn('label', subsystemLabel)
  ] : [
    baseColumn('label', subsystemLabel)
  ];

  return (
    <tscope.XmTable
      {...tableProps}
      pagination={false}
      scroll={{ x: 'max-content', y: '80vh' }}
      rowKey={(r) => r.item_id}
      columns={columns}
    />
  );
}

function isNotMultiRegex(data: ItemGroupPreview | ItemGroupPreview[]): data is ItemGroupPreview {
  return (data as ItemGroupPreview[]).length === undefined;
}

export type ApiCall<T> = (data: T, config?: AxiosRequestConfig<T>, strictAcl?: boolean) => Promise<AxiosResponse<ItemsResponseDTO, T>>;
export const regexApi: ApiCall<ItemGroupPreview> = (data, config, strictAcl = true) => postApi<ItemsResponseDTO, ItemGroupPreview>(API_URL.ARCHITECTURE + `/preview?rootParent=true&strict_acl=${strictAcl ? 'true' : 'false'}`, data, config);
export const multiRegexApi: ApiCall<ItemGroupPreview[]> = (data, config, strictAcl) => postApi<ItemsResponseDTO, ItemGroupPreview[]>(API_URL.ARCHITECTURE + `/multipreview?rootParent=true&strict_acl=${strictAcl ? 'true' : 'false'}`, data, config);

